/**
 * Validation utilities for mission modelling forms
 */

// Constants
export const NAME_MAX_LENGTH = 26;
export const ALLOWED_SPECIAL_CHARS = ['-', '_'];

/**
 * Validates a name field
 * @param value - The name value to validate
 * @returns { valid: boolean; error?: string }
 */
export const validateName = (value: string): { valid: boolean; error?: string } => {
  const trimmed = value.trim();

  // Check if empty
  if (!trimmed) {
    return { valid: false, error: 'Name is required' };
  }

  // Check length
  if (trimmed.length > NAME_MAX_LENGTH) {
    return { valid: false, error: getCharacterLimitMessage(NAME_MAX_LENGTH) };
  }

  // Check for invalid special characters
  const invalidChars = checkSpecialCharacters(trimmed);
  if (invalidChars) {
    return { valid: false, error: invalidChars };
  }

  return { valid: true };
};

/**
 * Check for invalid special characters
 * Only allows letters, numbers, hyphens (-), and underscores (_)
 * @param value - The string to check
 * @returns error message if invalid characters found, null otherwise
 */
export const checkSpecialCharacters = (value: string): string | null => {
  // Allow: letters, numbers, hyphen, underscore, and spaces
  const allowedPattern = /^[a-zA-Z0-9\-_ ]+$/;

  if (!allowedPattern.test(value)) {
    return `Special characters not allowed. Only letters, numbers, '-', '_' and spaces are allowed`;
  }

  return null;
};

/**
 * Get character limit message
 * @param limit - The character limit
 * @returns formatted message
 */
export const getCharacterLimitMessage = (limit: number): string => {
  return `Maximum allowed limit of ${limit} characters`;
};

/**
 * Validate name with length and character restrictions
 * Used during input to enforce limits
 * @param value - The raw input value
 * @param maxLength - Maximum allowed length
 * @returns trimmed and validated value
 */
export const enforceNameConstraints = (value: string, maxLength: number = NAME_MAX_LENGTH): string => {
  // Limit length
  let enforced = value.slice(0, maxLength);

  // Allow only alphanumeric, hyphen, underscore, and spaces
  enforced = enforced.replace(/[^a-zA-Z0-9\-_ ]/g, '');

  return enforced;
};

/**
 * Check if validation error exists for a field
 * @param errors - Validation errors object
 * @param fieldName - Field name to check
 * @returns true if error exists, false otherwise
 */
export const hasValidationError = (
  errors: Record<string, string>,
  fieldName: string
): boolean => {
  return !!errors[fieldName];
};

/**
 * Get validation error message for a field
 * @param errors - Validation errors object
 * @param fieldName - Field name to get error for
 * @returns error message or empty string
 */
export const getValidationErrorMessage = (
  errors: Record<string, string>,
  fieldName: string
): string => {
  return errors[fieldName] || '';
};
